import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import Tooltip from "@mui/material/Tooltip";

const MAX_LENGTH = 15;

export const isValidPhone = (phone) => {
  const regex = /^(?:\+254|0)(7|1)\d{8}$/;
  return regex.test(phone);
};

export const hasDuplicates = (arr) => {
  const normalized = arr.map((p) => p.trim());
  return new Set(normalized).size !== normalized.length;
};

export const addLeadingZeroIfNeeded = (value) => {
  if (!value) return value;

  // if exactly 9 digits and does NOT start with 0 or +254
  if (/^\d{9}$/.test(value)) {
    return "0" + value;
  }

  return value;
};

const MultiPhoneInput = ({
  name,
  values = [""],
  setFieldValue,
  requiredPrimary = false,
  allowEmpty = true,
}) => {
  const normalize = (phones) => {
    let cleaned = [...phones];

    // Remove trailing empty inputs except the first one
    while (cleaned.length > 1 && cleaned[cleaned.length - 1].trim() === "") {
      cleaned.pop();
    }

    // Primary (first) must not be empty
    if (cleaned[0].trim() === "") {
      return cleaned; // keep it empty, let validation handle it
    }

    const nonEmpty = cleaned.filter((p) => p.trim() !== "");

    const allValid = nonEmpty.every(isValidPhone);
    const noDuplicates = !hasDuplicates(nonEmpty);

    // Only add a new field if all current are valid
    if (
      allValid &&
      noDuplicates &&
      nonEmpty.length === cleaned.length &&
      nonEmpty.length > 0
    ) {
      cleaned.push("");
    }

    return cleaned;
  };

  const handleChange = (index, value) => {
    let cleaned = value.replace(/(?!^\+)[^\d]/g, "");

    const digitsOnly = cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;

    if (digitsOnly.length > MAX_LENGTH) return;

    const updated = [...values];
    updated[index] = cleaned;

    setFieldValue(name, normalize(updated));
  };

  const handleBlur = () => {
    setFieldValue(name, normalize(values));
  };

  const nonEmpty = values.filter((v) => v.trim() !== "");
  const duplicateExists = hasDuplicates(nonEmpty);

  return (
    <div>
      {values.map((phone, index) => {
        //console.log("Phone at index", index, "=", phone);
        const isPrimary = index === 0;

        const isDuplicate =
          phone.trim() !== "" && nonEmpty.filter((p) => p === phone).length > 1;

        const showError =
          (requiredPrimary && isPrimary && phone.trim() === "") ||
          (phone.length > 0 && !isValidPhone(phone)) ||
          isDuplicate;

        return (
          <div key={index} style={{ marginBottom: "1rem" }}>
            <TextField
              fullWidth
              variant="standard"
              label={
                isPrimary
                  ? "Primary Phone Number *"
                  : `Phone Number ${index + 1}`
              }
              value={phone}
              onChange={(e) => handleChange(index, e.target.value)}
              onBlur={handleBlur}
              error={showError}
              helperText={
                isPrimary && phone.trim() === ""
                  ? "Primary phone is required"
                  : isDuplicate
                    ? "Duplicate phone number"
                    : phone.length > 0 && !isValidPhone(phone)
                      ? "Enter valid phone number"
                      : ""
              }
              InputProps={{
                endAdornment: phone ? (
                  <InputAdornment position="end">
                    <Tooltip title="Remove number" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updated = [...values];
                          updated[index] = "";
                          setFieldValue(name, normalize(updated));
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export const normalizePhones = (phones) => {
  let cleaned = phones.map((p) => addLeadingZeroIfNeeded(p?.trim() || ""));

  while (cleaned.length > 1 && cleaned[cleaned.length - 1] === "") {
    cleaned.pop();
  }

  if (cleaned[0] === "") return cleaned;

  const nonEmpty = cleaned.filter((p) => p !== "");

  if (
    nonEmpty.every(isValidPhone) &&
    !hasDuplicates(nonEmpty) &&
    nonEmpty.length === cleaned.length
  ) {
    cleaned.push("");
  }

  return cleaned;
};




export const enforceValidLoginDay = ({
  lastLogin,
  navigate,
  redirectTo = "/my-account",
}) => {

  if (!lastLogin) {
    navigate(redirectTo, { state: { requirePhoneUpdate: true } });
    return false;
  }

  const lastLoginDate = new Date(lastLogin);

  if (isNaN(lastLoginDate.getTime())) {
    navigate(redirectTo, { state: { requirePhoneUpdate: true } });
    return false;
  }

  // Cutoff date
  const validLoginDay = new Date("2026-03-01T03:45:00+03:00");

  if (lastLoginDate < validLoginDay) {
    navigate(redirectTo, { state: { requirePhoneUpdate: true } });
    return false;
  }

  return true;
};

export default MultiPhoneInput;
